import { useState, FormEvent } from 'react';

interface CharterInquiryFormProps {
  yachtName: string;
  onClose: () => void;
}

export default function CharterInquiryForm({ yachtName, onClose }: CharterInquiryFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredDates: '',
    destination: '',
    guests: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://readdy.ai/api/form/d5k0acv9u36dslcghemg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          yacht: yachtName,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          preferredDates: formData.preferredDates,
          destination: formData.destination,
          guests: formData.guests,
          message: formData.message
        }).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-serif">Charter Inquiry</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center hover:opacity-70 transition-opacity cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          <div className="mb-6">
            <p className="text-sm text-gray-600">Yacht: <strong>{yachtName}</strong></p>
          </div>

          {submitStatus === 'success' ? (
            <div className="py-12 text-center">
              <i className="ri-check-line text-5xl text-green-600 mb-4"></i>
              <p className="text-lg">Thank you for your inquiry. We'll be in touch shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-readdy-form id="charter-inquiry-form">
              <div className="space-y-6">
                <div>
                  <label className="block text-xs tracking-wider mb-2">NAME *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 text-sm border border-gray-300 focus:outline-none focus:border-black transition-colors cursor-text"
                  />
                </div>

                <div>
                  <label className="block text-xs tracking-wider mb-2">EMAIL *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 text-sm border border-gray-300 focus:outline-none focus:border-black transition-colors cursor-text"
                  />
                </div>

                <div>
                  <label className="block text-xs tracking-wider mb-2">PHONE</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 text-sm border border-gray-300 focus:outline-none focus:border-black transition-colors cursor-text"
                  />
                </div>

                <div>
                  <label className="block text-xs tracking-wider mb-2">PREFERRED DATES</label>
                  <input
                    type="text"
                    name="preferredDates"
                    placeholder="e.g., July 15-22, 2024"
                    value={formData.preferredDates}
                    onChange={(e) => setFormData({ ...formData, preferredDates: e.target.value })}
                    className="w-full px-4 py-3 text-sm border border-gray-300 focus:outline-none focus:border-black transition-colors cursor-text"
                  />
                </div>

                <div>
                  <label className="block text-xs tracking-wider mb-2">DESTINATION</label>
                  <input
                    type="text"
                    name="destination"
                    value={formData.destination}
                    onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                    className="w-full px-4 py-3 text-sm border border-gray-300 focus:outline-none focus:border-black transition-colors cursor-text"
                  />
                </div>

                <div>
                  <label className="block text-xs tracking-wider mb-2">NUMBER OF GUESTS</label>
                  <input
                    type="number"
                    name="guests"
                    min="1"
                    value={formData.guests}
                    onChange={(e) => setFormData({ ...formData, guests: e.target.value })}
                    className="w-full px-4 py-3 text-sm border border-gray-300 focus:outline-none focus:border-black transition-colors cursor-text"
                  />
                </div>

                <div>
                  <label className="block text-xs tracking-wider mb-2">MESSAGE</label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-3 text-sm border border-gray-300 focus:outline-none focus:border-black transition-colors resize-none cursor-text"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                </div>

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200">
                    <p className="text-sm text-red-600">There was an error submitting your inquiry. Please try again.</p>
                  </div>
                )}

                <div className="flex gap-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-6 py-4 bg-black text-white text-sm tracking-wider hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'SUBMITTING...' : 'SUBMIT INQUIRY'}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-6 py-4 border border-black text-sm tracking-wider hover:bg-black hover:text-white transition-colors whitespace-nowrap cursor-pointer"
                  >
                    CANCEL
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
